import type { TranscriptSegmentData } from '@/lib/recordings/parse-transcript';
import type { SpeakerLabel } from './types/types';
import { resolveSpeakerName } from './resolve-speaker-name';

export interface SpeakerStat {
  speaker: string;
  /** Total talk time for this speaker, in milliseconds. */
  durationMs: number;
  /** Share of total talk time, rounded to a whole percent. */
  pct: number;
}

/**
 * Aggregates transcript segments into per-speaker talk-time stats, sorted by
 * talk time descending. Speakers are grouped + labelled by their RESOLVED
 * display name (applying `speakerLabels` overrides) so renaming a speaker
 * updates the chips. `pct` is each speaker's share, rounded to an integer.
 */
export function computeSpeakerStats(
  segments: TranscriptSegmentData[],
  speakerLabels: Record<string, SpeakerLabel> = {},
  attendeeNameByEmail?: Record<string, string>,
): SpeakerStat[] {
  const durations = new Map<string, number>();
  let total = 0;
  for (const s of segments) {
    const speaker = resolveSpeakerName(s, speakerLabels, attendeeNameByEmail);
    const dur = Math.max(0, s.end_ms - s.start_ms);
    durations.set(speaker, (durations.get(speaker) ?? 0) + dur);
    total += dur;
  }

  return [...durations.entries()]
    .map(([speaker, durationMs]) => ({
      speaker,
      durationMs,
      pct: total > 0 ? Math.round((durationMs / total) * 100) : 0,
    }))
    .sort((a, b) => b.durationMs - a.durationMs);
}
